/**
 * Scene System V2 - Flow Integration
 *
 * Integration layer between Scene System V2 and existing Flow canvas
 */

import { deviceSceneCapabilities } from '../core/DeviceSceneCapabilities';
import { sceneActionEngine } from '../core/SceneActionEngine';
import { sceneConditionEngine } from '../core/SceneConditionEngine';
import type { FlowNode, FlowEdge, NodeData } from '../types/flow-types';
import type { ISceneObj } from '../../../types/SceneType';
import type {
	DeviceSceneCapabilities,
	SceneExecutionContext,
	SceneAction,
	SceneCondition,
	SceneTrigger,
	SceneParameter,
} from '../types/scene-capabilities';
import { get } from 'svelte/store';
import { devices } from '../../../stores';
import type { Device } from '../../../types/Device';

export interface V2FlowIntegration {
	enhanceNodeData(node: FlowNode): Promise<FlowNode>;
	validateSceneCompatibility(scene: ISceneObj): Promise<ValidationResult>;
	convertToV2Actions(scene: ISceneObj): Promise<V2Action[]>;
	getDeviceCapabilities(deviceType: string): DeviceSceneCapabilities | null;
}

export interface ValidationResult {
	isValid: boolean;
	errors: string[];
	warnings: string[];
	v2Compatible: boolean;
	upgradeRecommendations: string[];
}

export interface V2Action {
	deviceType: string;
	actionName: string;
	parameters: Record<string, any>;
	validationResult: any;
}

export interface NodeCapabilityInfo {
	capabilities: Record<string, SceneAction | SceneCondition | SceneTrigger>;
	deviceModel: string;
	channels: number;
	hasMultipleChannels: boolean;
}

export interface CapabilityTestResult {
	success: boolean;
	result?: any;
	error?: string;
}

export class SceneSystemV2FlowIntegration implements V2FlowIntegration {
	private initialized = false;

	/**
	 * Initialize the V2 integration system
	 */
	public async initialize(): Promise<void> {
		if (this.initialized) return;

		console.log('🔧 Initializing Scene System V2 Flow Integration...');

		try {
			await deviceSceneCapabilities.initialize();
			this.initialized = true;
			console.log('✅ Scene System V2 Flow Integration initialized successfully');
		} catch (error) {
			console.error('❌ Failed to initialize Scene System V2 Flow Integration:', error);
			throw error;
		}
	}

	/**
	 * Enhance Flow node data with V2 capabilities
	 */
	public async enhanceNodeData(node: FlowNode): Promise<FlowNode> {
		if (!this.initialized) {
			await this.initialize();
		}

		// Clone the node to avoid mutations
		const enhancedNode = { ...node };

		try {
			// Check if node has device information
			const nodeData = node.data as NodeData;

			if (nodeData?.sceneData?.device) {
				const deviceType = this.extractDeviceType(nodeData.sceneData.device);

				if (deviceType) {
					// Get V2 capabilities for this device
					const capabilities = deviceSceneCapabilities.getCapabilities(deviceType);

					if (capabilities) {
						// Enhance node data with V2 capabilities
						enhancedNode.data = {
							...nodeData,
							v2Capabilities: capabilities,
							v2DeviceType: deviceType,
							v2Enhanced: true,
						};

						console.log(
							`✅ Enhanced node ${node.id} with V2 capabilities for ${deviceType}`,
						);
					}
				}
			}
		} catch (error) {
			console.warn(`⚠️ Failed to enhance node ${node.id}:`, error);
		}

		return enhancedNode;
	}

	/**
	 * Validate if scene is compatible with V2 system
	 */
	public async validateSceneCompatibility(scene: ISceneObj): Promise<ValidationResult> {
		if (!this.initialized) {
			await this.initialize();
		}

		const result: ValidationResult = {
			isValid: true,
			errors: [],
			warnings: [],
			v2Compatible: true,
			upgradeRecommendations: [],
		};

		try {
			const doBlk = scene.do as any;
			const hasValidDoShape =
				(Array.isArray(doBlk) && doBlk.length >= 0) ||
				(doBlk &&
					typeof doBlk === 'object' &&
					!Array.isArray(doBlk) &&
					Array.isArray(doBlk.blk) &&
					doBlk.blk.length >= 0);

			if (!scene.do || !hasValidDoShape) {
				result.errors.push('Scene must have valid "do" actions (array or { blk: [...] })');
				result.isValid = false;
			}

			const doForWalk =
				doBlk && typeof doBlk === 'object' && !Array.isArray(doBlk) && Array.isArray(doBlk.blk)
					? doBlk.blk
					: Array.isArray(doBlk)
						? doBlk
						: doBlk != null
							? [doBlk]
							: [];

			if (scene.do) {
				for (const action of doForWalk) {
					const actionValidation = await this.validateSceneAction(action);

					if (!actionValidation.isValid) {
						result.errors.push(...actionValidation.errors);
						result.warnings.push(...actionValidation.warnings);
					}

					if (!actionValidation.v2Compatible) {
						result.v2Compatible = false;
						result.upgradeRecommendations.push(...actionValidation.upgradeRecommendations);
					}
				}
			}

			// Validate conditions if present
			if (scene.if) {
				const conditionValidation = await this.validateSceneConditions(scene.if);

				if (!conditionValidation.isValid) {
					result.errors.push(...conditionValidation.errors);
					result.warnings.push(...conditionValidation.warnings);
				}
			}

			result.isValid = result.errors.length === 0;
		} catch (error) {
			result.errors.push(`Validation failed: ${error.message}`);
			result.isValid = false;
		}

		return result;
	}

	/**
	 * Convert legacy scene actions to V2 format
	 */
	public async convertToV2Actions(scene: ISceneObj): Promise<V2Action[]> {
		if (!this.initialized) {
			await this.initialize();
		}

		const v2Actions: V2Action[] = [];

		try {
			const rawDo = scene.do as any;
			const doForWalk =
				rawDo && typeof rawDo === 'object' && !Array.isArray(rawDo) && Array.isArray(rawDo.blk)
					? rawDo.blk
					: Array.isArray(rawDo)
						? rawDo
						: rawDo != null
							? [rawDo]
							: [];

			if (doForWalk.length > 0) {
				for (const action of doForWalk) {
					const v2Action = await this.convertSingleAction(action);
					if (v2Action) {
						v2Actions.push(v2Action);
					}
				}
			}
		} catch (error) {
			console.error('Failed to convert scene to V2 actions:', error);
		}

		return v2Actions;
	}

	/**
	 * Get device capabilities for a device type
	 */
	public getDeviceCapabilities(deviceType: string): DeviceSceneCapabilities | null {
		if (!this.initialized) {
			console.warn('V2 Integration not initialized, cannot get device capabilities');
			return null;
		}

		return deviceSceneCapabilities.getCapabilities(deviceType);
	}

	/**
	 * Get available device types with V2 support
	 */
	public getAvailableDeviceTypes(): string[] {
		if (!this.initialized) {
			return [];
		}

		return deviceSceneCapabilities.getLoadedDeviceTypes();
	}

	/**
	 * Test V2 action execution (dry run)
	 

	/**
	 * Get device capabilities filtered by node type (action/condition/trigger)
	 */
	public getDeviceCapabilitiesForNode(
		deviceId: string,
		nodeType: 'action' | 'condition' | 'trigger',
	): NodeCapabilityInfo | null {
		if (!this.initialized) {
			console.warn('V2 Integration not initialized');
			return null;
		}

		// Get device from store
		const devicesStore = get(devices);
		const device = devicesStore[deviceId];

		if (!device) {
			console.warn(`Device ${deviceId} not found in store`);
			return null;
		}

		// Get device model/type
		const deviceModel = device.type || device.model;

		if (!deviceModel) {
			console.warn(`Device ${deviceId} has no model/type`);
			return null;
		}

		// Load capabilities
		const capabilities = deviceSceneCapabilities.getCapabilities(deviceModel);

		if (!capabilities) {
			console.warn(`No scene capabilities found for device model: ${deviceModel}`);
			return null;
		}

		// Get channel count
		const channels = this.getDeviceChannelCount(device);

		// Filter capabilities by node type
		let filteredCapabilities: Record<string, any> = {};

		switch (nodeType) {
			case 'action':
				filteredCapabilities = capabilities.actions || {};
				break;
			case 'condition':
				filteredCapabilities = capabilities.conditions || {};
				break;
			case 'trigger':
				filteredCapabilities = capabilities.triggers || {};
				break;
		}

		return {
			capabilities: filteredCapabilities,
			deviceModel,
			channels,
			hasMultipleChannels: channels > 1,
		};
	}

	/**
	 * Get channels for a device
	 */
	public getChannelsForDevice(deviceId: string): number[] {
		const devicesStore = get(devices);
		const device = devicesStore[deviceId];

		if (!device) {
			return [0];
		}

		const channelCount = this.getDeviceChannelCount(device);
		return Array.from({ length: channelCount }, (_, i) => i);
	}

	/**
	 * Get parameters for a specific capability
	 */
	public getParametersForCapability(
		deviceModel: string,
		nodeType: 'action' | 'condition' | 'trigger',
		capabilityName: string,
		channel?: number,
	): Record<string, SceneParameter> | null {
		if (!this.initialized) {
			return null;
		}

		const capabilities = deviceSceneCapabilities.getCapabilities(deviceModel);

		if (!capabilities) {
			return null;
		}

		let capability: SceneAction | SceneCondition | SceneTrigger | undefined;

		switch (nodeType) {
			case 'action':
				capability = capabilities.actions?.[capabilityName];
				break;
			case 'condition':
				capability = capabilities.conditions?.[capabilityName];
				break;
			case 'trigger':
				capability = capabilities.triggers?.[capabilityName];
				break;
		}

		if (!capability) {
			return null;
		}

		return capability.parameters || {};
	}

	/**
	 * Test a capability with given parameters
	 */
	public async testCapability(
		deviceId: string,
		nodeType: 'action' | 'condition' | 'trigger',
		channel: number,
		capabilityName: string,
		parameters: Record<string, any>,
	): Promise<CapabilityTestResult> {
		if (!this.initialized) {
			await this.initialize();
		}

		try {
			const devicesStore = get(devices);
			const device = devicesStore[deviceId];

			if (!device) {
				return {
					success: false,
					error: 'Device not found',
				};
			}

			const deviceModel = device.type || device.model;

			if (!deviceModel) {
				return {
					success: false,
					error: 'Device model not found',
				};
			}

			const context = deviceSceneCapabilities.createExecutionContext(deviceId, deviceModel, {
				channel,
				device,
			});

			let result;

			switch (nodeType) {
				case 'action':
					result = await sceneActionEngine.executeAction(context, capabilityName, parameters, {
						dryRun: true,
					});
					break;
				case 'condition':
					result = await sceneConditionEngine.evaluateCondition(
						context,
						capabilityName,
						parameters,
					);
					break;
				case 'trigger':
					// Triggers can't be tested directly, just validate
					result = { success: true, message: 'Trigger configuration is valid' };
					break;
			}

			return {
				success: result.success || true,
				result,
			};
		} catch (error) {
			return {
				success: false,
				error: error.message || 'Test failed',
			};
		}
	}
	public async testActionExecution(
		deviceType: string,
		actionName: string,
		parameters: Record<string, any>,
	): Promise<any> {
		if (!this.initialized) {
			await this.initialize();
		}

		const context = deviceSceneCapabilities.createExecutionContext('test-device', deviceType);

		return await sceneActionEngine.executeAction(context, actionName, parameters, { dryRun: true });
	}

	// Private helper methods

	private getDeviceChannelCount(device: Device): number {
		// Get the active mode
		const mode = device.mode || 'relay';

		// Try to get channel count from device config
		if (device.channels && typeof device.channels === 'number') {
			return device.channels;
		}

		// Fallback to checking modes
		if (device.modes && device.modes[mode]) {
			return device.modes[mode].channels || 1;
		}

		// Default to single channel
		return 1;
	}

	private extractDeviceType(device: any): string | null {
		// Extract device type from scene device object
		if (typeof device === 'string') {
			return device;
		}

		if (device && typeof device === 'object') {
			return device.type || device.model || device.deviceType || null;
		}

		return null;
	}

	private async validateSceneAction(action: any): Promise<ValidationResult> {
		const result: ValidationResult = {
			isValid: true,
			errors: [],
			warnings: [],
			v2Compatible: false,
			upgradeRecommendations: [],
		};

		// Basic action structure validation
		if (!action.device) {
			result.errors.push('Action must specify device');
			result.isValid = false;
		}

		if (!action.actions || !Array.isArray(action.actions)) {
			result.errors.push('Action must have actions array');
			result.isValid = false;
		}

		// Check V2 compatibility
		const deviceType = this.extractDeviceType(action.device);
		if (deviceType && deviceSceneCapabilities.hasCapabilities(deviceType)) {
			result.v2Compatible = true;
		} else {
			result.upgradeRecommendations.push(`Add V2 capabilities for device type: ${deviceType}`);
		}

		return result;
	}

	private async validateSceneConditions(conditions: any): Promise<ValidationResult> {
		const result: ValidationResult = {
			isValid: true,
			errors: [],
			warnings: [],
			v2Compatible: true,
			upgradeRecommendations: [],
		};

		// Conditions validation logic would go here
		// For now, assume conditions are valid

		return result;
	}

	private async convertSingleAction(action: any): Promise<V2Action | null> {
		try {
			const deviceType = this.extractDeviceType(action.device);

			if (!deviceType) {
				console.warn('Cannot convert action without device type');
				return null;
			}

			// Simple conversion - in real implementation this would be more sophisticated
			const actionName = action.actions?.[0]?.name || 'turn';
			const parameters = action.actions?.[0] || {};

			// Validate with V2 system
			const context = deviceSceneCapabilities.createExecutionContext(
				action.device?.id || 'unknown',
				deviceType,
			);

			const validationResult = await sceneActionEngine.executeAction(
				context,
				actionName,
				parameters,
				{ validateOnly: true },
			);

			return {
				deviceType,
				actionName,
				parameters,
				validationResult,
			};
		} catch (error) {
			console.error('Failed to convert action:', error);
			return null;
		}
	}
}

// Export singleton instance
export const sceneSystemV2FlowIntegration = new SceneSystemV2FlowIntegration();
